import { Component, input, output, signal, inject, LOCALE_ID, DestroyRef, effect, computed } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Attribute } from '../../../../../../../pages/admin-dashboard/products/add-sub-product/sub-products.model';
import { DropdownArrow } from '../../../../../../../shared/ui/icons/dropdown-arrow/dropdown-arrow';

@Component({
  selector: 'app-attribute-selection',
  standalone: true,
  imports: [ReactiveFormsModule, DropdownArrow],
  templateUrl: './attribute-selection.html',
})
export class AttributeSelection {
  private fb = inject(FormBuilder);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  attributes = input.required<Attribute[]>();
  existingCombinations = input<Record<string, string>[]>([]);
  onSelectionChange = output<Record<string, string> | null>();

  form = new FormGroup({});
  duplicateError = signal(false);
  duplicateIndex = signal<number | null>(null);

  isArabic = computed(() => this.locale.startsWith('ar'));

  getLabel(obj: { titleEn: string | null; titleAr: string | null }): string {
    return this.isArabic() ? obj.titleAr || obj.titleEn || '' : obj.titleEn || '';
  }

  constructor() {
    effect(() => {
      const attrs = this.attributes();
      const controls: Record<string, any> = {};
      attrs.forEach((attr) => {
        controls[attr.attributeKey.titleEn ?? ''] = this.fb.control('', Validators.required);
      });
      this.form = this.fb.group(controls);

      this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vals) => {
        const isDuplicate = this.checkDuplicate(vals);
        this.onSelectionChange.emit(isDuplicate ? null : vals);
      });
    });
  }

  private checkDuplicate(vals: Record<string, string>): boolean {
    const hasAnySelected = Object.values(vals).some((v) => !!v);
    if (!hasAnySelected) {
      this.duplicateError.set(false);
      this.duplicateIndex.set(null);
      return false;
    }
    const idx = this.existingCombinations().findIndex((combo) =>
      Object.keys(vals).every((key) => String(combo[key]) === String(vals[key])),
    );
    const isDuplicate = idx !== -1;
    this.duplicateError.set(isDuplicate);
    this.duplicateIndex.set(isDuplicate ? idx : null);
    return isDuplicate;
  }

  markAllTouched(): void {
    this.form.markAllAsTouched();
  }
  reset(): void {
    this.form.reset();
    this.duplicateError.set(false);
    this.duplicateIndex.set(null);
  }
  isValid(): boolean {
    return this.form.valid && !this.duplicateError();
  }
}
